(() => {
  if (globalThis.FormwynnProductMerchandising) return;

  const normalize = (value) => String(value ?? '').trim().toLowerCase();

  const variantForOptions = (variants, selectedOptions) => variants.find((variant) => (
    Array.isArray(variant.options)
      && variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => (
        normalize(value) === normalize(selectedOptions[index])
      ))
  ));

  const canQuickAdd = (variant, selectedOptions) => (
    Boolean(variant?.available)
    && Array.isArray(selectedOptions)
    && selectedOptions.length > 0
    && selectedOptions.every((value) => normalize(value) !== '')
  );

  const recommendationUrl = (baseUrl, productId, sectionId, limit = 4) => {
    const separator = String(baseUrl).includes('?') ? '&' : '?';
    return `${baseUrl}${separator}section_id=${encodeURIComponent(sectionId)}`
      + `&product_id=${encodeURIComponent(productId)}`
      + `&limit=${encodeURIComponent(limit)}&intent=related`;
  };

  const shouldShowRecommendations = (products) => (
    Array.isArray(products) && products.length > 0
  );

  const VIEWED_KEY = 'formwynn:viewed-products:v1';

  const updateViewed = (history, currentHandle, maximum = 8) => {
    const unique = [];
    (Array.isArray(history) ? history : []).forEach((handle) => {
      const cleanHandle = normalize(handle);
      if (cleanHandle && !unique.includes(cleanHandle)) unique.push(cleanHandle);
    });
    const current = normalize(currentHandle);
    const withoutCurrent = current
      ? unique.filter((handle) => handle !== current)
      : unique;
    if (current) withoutCurrent.unshift(current);
    return withoutCurrent.slice(0, Math.max(0, Number(maximum) || 0));
  };

  const addVariantToCart = async (variantId) => {
    const marketRoot = String(globalThis.Shopify?.routes?.root || '/')
      .replace(/\/+$/, '');
    const response = await fetch(`${marketRoot}/cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
      }),
    });

    if (!response.ok) {
      let message = 'Unable to add this selection.';
      try {
        const payload = await response.json();
        message = payload.description || payload.message || message;
      } catch {
        // Keep the customer-facing fallback for non-JSON Shopify errors.
      }
      throw new Error(message);
    }

    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const initializedCards = new WeakSet();

  const initCard = (card) => {
    if (initializedCards.has(card)) return;

    let product;
    try {
      product = JSON.parse(card.dataset.productJson || '{}');
    } catch {
      return;
    }
    if (!Array.isArray(product.variants)) return;

    initializedCards.add(card);
    const optionCount = Array.isArray(product.options)
      ? product.options.length
      : product.variants[0]?.options?.length || 0;
    const selectedOptions = Array(optionCount).fill('');
    const variantInput = card.querySelector('[data-merch-variant-id]');
    const addButton = card.querySelector('[data-merch-action="quick-add"]');
    const status = card.querySelector('[data-merch-status]');
    let selectedVariant;

    if (!addButton) return;

    const setStatus = (message) => {
      if (status) status.textContent = message;
    };

    const sync = ({ preserveStatus = false } = {}) => {
      selectedVariant = variantForOptions(product.variants, selectedOptions);
      const complete = selectedOptions.length > 0
        && selectedOptions.every((value) => normalize(value) !== '');
      const available = canQuickAdd(selectedVariant, selectedOptions);

      if (variantInput) {
        variantInput.value = selectedVariant ? String(selectedVariant.id) : '';
      }
      addButton.disabled = !available;
      addButton.textContent = available
        ? 'Add to bag'
        : complete && selectedVariant && !selectedVariant.available
          ? 'Sold out'
          : complete
            ? 'Unavailable'
            : 'Select options';
      if (!preserveStatus) setStatus('');
    };

    card.addEventListener('click', async (event) => {
      const control = event.target.closest?.('[data-merch-action]');
      if (!control) return;
      const action = control.dataset.merchAction;

      if (action === 'option') {
        const optionIndex = Number(control.dataset.optionIndex);
        if (
          !Number.isInteger(optionIndex)
          || optionIndex < 0
          || optionIndex >= selectedOptions.length
          || !control.dataset.optionValue
        ) return;

        selectedOptions[optionIndex] = control.dataset.optionValue;
        const group = control.closest('[data-merch-option]');
        group?.querySelectorAll('[data-merch-action="option"]').forEach((button) => {
          button.setAttribute(
            'aria-pressed',
            String(button.dataset.optionValue === control.dataset.optionValue),
          );
        });
        const label = group?.querySelector('[data-merch-option-value]');
        if (label) label.textContent = control.dataset.optionValue;
        sync();
        return;
      }

      if (action !== 'quick-add' || !canQuickAdd(selectedVariant, selectedOptions)) return;

      addButton.disabled = true;
      addButton.textContent = 'Adding...';
      setStatus('');
      try {
        await addVariantToCart(selectedVariant.id);
        setStatus('Added to bag.');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'Unable to add this selection.');
      } finally {
        sync({ preserveStatus: true });
      }
    });

    sync();
  };

  const initCardsWithin = (target) => {
    if (!target) return;
    const cards = [];
    if (target.matches?.('[data-merch-product]')) cards.push(target);
    target.querySelectorAll?.('[data-merch-product]').forEach((card) => cards.push(card));
    [...new Set(cards)].forEach(initCard);
  };

  const initializedRecommendations = new WeakSet();

  const initRecommendations = async (root) => {
    if (
      initializedRecommendations.has(root)
      || root.dataset.recommendationsLoaded === 'true'
    ) return;
    initializedRecommendations.add(root);
    root.hidden = true;

    const baseUrl = root.dataset.recommendationsUrl;
    const productId = root.dataset.productId;
    const sectionId = root.dataset.sectionId;
    const limit = Number(root.dataset.limit || 4);
    if (!baseUrl || !productId || !sectionId) return;

    try {
      const response = await fetch(
        recommendationUrl(baseUrl, productId, sectionId, limit),
        { headers: { Accept: 'text/html' } },
      );
      if (!response.ok) return;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-recommendations-content]');
      const cards = content
        ? [...content.querySelectorAll('[data-merch-product]')]
        : [];
      if (!content || !shouldShowRecommendations(cards)) return;

      root.innerHTML = content.innerHTML;
      root.dataset.recommendationsLoaded = 'true';
      root.hidden = false;
      initCardsWithin(root);
    } catch {
      root.hidden = true;
    }
  };

  const initRecommendationsWithin = (target) => {
    if (!target) return;
    const roots = [];
    if (target.matches?.('[data-product-recommendations]')) roots.push(target);
    target.querySelectorAll?.('[data-product-recommendations]')
      .forEach((root) => roots.push(root));
    [...new Set(roots)].forEach(initRecommendations);
  };

  const initializedRecentlyViewed = new WeakSet();

  const createRecentlyViewedCard = (product, currency) => {
    const article = document.createElement('article');
    article.className = 'fw-merch-card';
    article.dataset.source = 'recently-viewed';

    const link = document.createElement('a');
    link.className = 'fw-merch-card__media';
    link.href = product.url;
    const featuredImage = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.src;
    if (featuredImage) {
      const image = document.createElement('img');
      image.src = featuredImage;
      image.alt = product.title || '';
      image.loading = 'lazy';
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'fw-merch-card__placeholder';
      placeholder.textContent = 'Image unavailable';
      link.append(placeholder);
    }

    const details = document.createElement('div');
    details.className = 'fw-merch-card__details';
    const title = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.textContent = product.title || '';
    title.append(titleLink);

    const meta = document.createElement('div');
    meta.className = 'fw-merch-card__meta';
    const price = document.createElement('strong');
    try {
      price.textContent = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
      }).format(Number(product.price || 0) / 100);
    } catch {
      price.textContent = `${currency} ${(Number(product.price || 0) / 100).toFixed(2)}`;
    }
    meta.append(price);
    details.append(title, meta);
    article.append(link, details);
    return article;
  };

  const initRecentlyViewed = async (root) => {
    if (initializedRecentlyViewed.has(root)) return;
    initializedRecentlyViewed.add(root);
    root.hidden = true;

    const currentHandle = normalize(root.dataset.currentHandle);
    if (!currentHandle) return;
    const limit = Math.min(4, Math.max(1, Number(root.dataset.limit || 4)));
    let history = [];
    try {
      const stored = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
      if (Array.isArray(stored)) history = stored;
    } catch {
      history = [];
    }

    const previousHandles = updateViewed(history, '', 8)
      .filter((handle) => handle !== currentHandle)
      .slice(0, limit);
    try {
      localStorage.setItem(
        VIEWED_KEY,
        JSON.stringify(updateViewed(history, currentHandle, 8)),
      );
    } catch {
      // Browsing can continue when storage is unavailable or full.
    }
    if (!previousHandles.length) return;

    const marketRoot = String(globalThis.Shopify?.routes?.root || '/')
      .replace(/\/+$/, '');
    const products = await Promise.all(previousHandles.map(async (handle) => {
      try {
        const response = await fetch(
          `${marketRoot}/products/${encodeURIComponent(handle)}.js`,
          { headers: { Accept: 'application/json' } },
        );
        if (!response.ok) return null;
        const product = await response.json();
        if (!product?.title) return null;
        return {
          ...product,
          url: product.url || `${marketRoot}/products/${handle}`,
        };
      } catch {
        return null;
      }
    }));
    const visibleProducts = products.filter(Boolean);
    if (!visibleProducts.length) return;

    const grid = root.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;
    grid.replaceChildren(
      ...visibleProducts.map((product) => (
        createRecentlyViewedCard(product, root.dataset.currency || 'GBP')
      )),
    );
    root.hidden = false;
  };

  const initRecentlyViewedWithin = (target) => {
    if (!target) return;
    const roots = [];
    if (target.matches?.('[data-recently-viewed]')) roots.push(target);
    target.querySelectorAll?.('[data-recently-viewed]').forEach((root) => {
      roots.push(root);
    });
    [...new Set(roots)].forEach(initRecentlyViewed);
  };

  globalThis.FormwynnProductMerchandising = Object.freeze({
    VIEWED_KEY,
    addVariantToCart,
    canQuickAdd,
    initCard,
    recommendationUrl,
    shouldShowRecommendations,
    updateViewed,
    variantForOptions,
  });

  initCardsWithin(document);
  initRecommendationsWithin(document);
  initRecentlyViewedWithin(document);
  document.addEventListener('shopify:section:load', (event) => {
    initCardsWithin(event.target);
    initRecommendationsWithin(event.target);
    initRecentlyViewedWithin(event.target);
  });
})();
